import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Layout from "../components/Layout";
import Header from "../components/Header";
import ActionButton from "../components/ActionButton";
import ProgressBar from "../components/ProgressBar";
import PageTransition from "../components/PageTransition";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDispatch } from "react-redux";
import { setVideoListingUrl, resetVideo } from "@/store/features/videoSlice";
import { resetListing } from "@/store/features/listingSlice";
import { setListingJobId } from "@/store/features/listingSlice";
import { useNavigate } from "react-router-dom";
import { store } from "@/store";
import { setAvatars } from "@/store/features/appSlice";
import { useQuery, useMutation } from "@apollo/client";
import { GET_AVATARS, CREATE_SCRAPING_JOB } from "@/integrations/apollo/operations";

// Define a type for the dispatch function based on the store
type AppDispatch = typeof store.dispatch;

const Index = () => {
  const [propertyUrl, setPropertyUrl] = useState<string>("");
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const isMobile = useIsMobile();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { data: avatarsData } = useQuery(GET_AVATARS);
  const [createScrapingJob] = useMutation(CREATE_SCRAPING_JOB);

  useEffect(() => {
    dispatch(resetVideo());
    dispatch(resetListing());
  }, []);

  useEffect(() => {
    if (!avatarsData) return;

    dispatch(setAvatars(avatarsData.avatars));
  }, [avatarsData]);

  const validateUrl = async () => {
    setIsValidating(true);

    if (!propertyUrl) {
      toast.error("Please enter a property URL");

      setIsValid(false);
      setIsValidating(false);

      return;
    } else if (!/^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/.test(propertyUrl)) {
      toast.error("Please enter a valid URL");

      setIsValid(false);
      setIsValidating(false);

    } else {
      dispatch(setVideoListingUrl(propertyUrl));

      sessionStorage.setItem('scraping_start_time', `${+new Date()}`);

      try {
        const { data: jobData } = await createScrapingJob({ variables: { scraped_website_url: propertyUrl } });

        if (!jobData) {
          throw new Error('Failed to create scraping job');
        }

        dispatch(setListingJobId(jobData.triggerScrapeWebsiteDataJob.extractionJobId));

        setTimeout(() => {
          toast.success('URL validated successfully');

          navigate('/new-video/actor');
        }, 1500);
      } catch (error) {
        console.error('Error creating job:', error);
        toast.error('Error validating URL. Please try again.');
      } finally {
        setIsValidating(false);
      }
    }
  };
  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPropertyUrl(e.target.value);
    setIsValid(null);
  };

  return (
    <Layout>
      <PageTransition>
        <Header />
        <ProgressBar />

        <motion.div className={`space-y-4 ${isMobile ? 'mt-10' : 'mt-6'}`} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }}>
          <div className="text-center mb-4">
            <h2 className="font-bold tracking-tight mb-2 text-xl text-center">STEP 1: Let's Find Your Property</h2>
            <p className="text-muted-foreground text-balance text-center">Start by entering your property link. We'll extract the key details to create a compelling video ad.</p>
          </div>

          <div className="glass rounded-xl p-6 space-y-4">
            <div className="space-y-2">
              <label htmlFor="propertyUrl" className="text-sm font-medium">
                Link to Your Property
              </label>
              <div className="relative">
                <motion.div className={`
                    absolute inset-0 rounded-md pointer-events-none
                    ${isValid === true ? "ring-2 ring-green-500" : isValid === false ? "ring-2 ring-red-500" : ""}
                  `} animate={isValid === true ? {
                boxShadow: ["0 0 0 0 rgba(34, 197, 94, 0)", "0 0 0 4px rgba(34, 197, 94, 0.2)", "0 0 0 0 rgba(34, 197, 94, 0)"]
              } : {}} transition={{
                duration: 1.5,
                ease: "easeInOut"
              }} />
                <input id="propertyUrl" type="url" value={propertyUrl} onChange={handleLinkChange} placeholder="https://www.airbnb.com/rooms/123456" className="w-full h-10 px-4 py-2 pr-10 rounded-md border bg-background focus:outline-none input-focus" disabled={isValidating} />
                {isValidating && <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <svg className="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>}
                {isValid === true && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>}
                {isValid === false && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>}
              </div>
              <p className="text-xs text-muted-foreground">
                Enter your Airbnb or PropertySimple property URL
              </p>
            </div>

            <div className="pt-2 flex flex-col space-y-2">
              {isValid !== true ? <ActionButton fullWidth onClick={validateUrl} disabled={isValidating}>
                  {isValidating ? "Validating..." : "Continue"}
                </ActionButton> : <ActionButton fullWidth to="/new-video/actor">
                  Continue to Actor Selection
                </ActionButton>}

              <div className="flex justify-center">
                <ActionButton variant="back" disabled={true}>
                  This is the first step
                </ActionButton>
              </div>
            </div>
          </div>

          <div className="glass rounded-xl p-6">
            <h3 className="font-medium mb-3">What happens next?</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="m16 12-4 4-4-4" />
                    <path d="M12 8v8" />
                  </svg>
                </div>
                <span>We'll extract key details from your property</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <span>You'll select an actor for your video</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <line x1="10" y1="9" x2="8" y2="9" />
                  </svg>
                </div>
                <span>Choose from multiple script options</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="14" x="2" y="5" rx="2" />
                    <line x1="2" x2="22" y1="10" y2="10" />
                  </svg>
                </div>
                <span>Complete a simple checkout ($99 per video)</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m22 8-6 4 6 4V8Z" />
                    <rect width="14" height="12" x="2" y="6" rx="2" />
                    <path d="M6 12h4" />
                    <path d="M10 12v4" />
                  </svg>
                </div>
                <span>Receive your professional video ad</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </PageTransition>
    </Layout>
  );
};

export default Index;
